// ABOUTME: Configures WhatsApp template bulk sends and previews recipient eligibility.
// ABOUTME: Shows actionable setup blockers for screening-questionnaire requests.
import React from "react";
import {
    Button,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@material-ui/core";
import { navigate } from "@gatsbyjs/reach-router";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import useList from "../..";
import useGetMetadata from "customHooks/useGetMetadata";
import { useAuthorization, useAuthState } from "_ReactContext/AuthContext";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    analyseWhatsappTemplate,
    getApiErrorMessage,
    getWhatsappTemplateName,
    getWhatsappTemplateSendContext
} from "Pages/Applicant/ApplicantView/ApplicantWhatsapp/helpers";
import {
    BulkActionsType,
    IListBulk,
    useJobListDropdown
} from "./helperBulkActions";
import {
    getWhatsappScreeningQuestionnaireRequestPreviewTemplate,
    getWhatsappScreeningQuestionnaireRequestTemplateSetup
} from "./whatsappScreeningQuestionnaireRequest";

interface IProps {
    formikRef: React.MutableRefObject<any>;
    listBulk: IListBulk[];
    setStepBulk: (value: React.SetStateAction<number>) => void;
    setLoadingButtons: React.Dispatch<React.SetStateAction<boolean>>;
    fromJobViewJobId?: string;
    bulkActionType?: BulkActionsType;
    fixedGlobalTemplateKey?: string;
}

interface PreviewData {
    total: number;
    eligibleCount: number;
    blockedCount: number;
    eligibleIds: string[];
    requiredConsentCategory: string;
    items: PreviewItem[];
    reasons: Record<string, number>;
    configurationBlockCode?: string | null;
}

interface PreviewItem {
    id: string;
    applicantId: string;
    name: string;
    email: string;
    mobileNumber: string;
    eligible: boolean;
    reasonCode: string;
    reason: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2)
        },
        stat: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            padding: theme.spacing(1.5),
            minHeight: 76
        },
        statValue: {
            fontWeight: 700
        },
        previewBox: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            padding: theme.spacing(2)
        },
        recipientsTableWrapper: {
            marginTop: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            overflowX: "auto"
        },
        recipientsTable: {
            minWidth: 760
        }
    })
);

const getTemplateKey = (template: Compleo.IObject) =>
    `${getWhatsappTemplateName(template)}#${template.language || "pt_BR"}`;

const getTranslationOrFallback = (t: any, key: string, fallback: string) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
};

const getPreviewReasonLabel = (t: any, item: PreviewItem) => {
    if (item.eligible) {
        return "-";
    }

    return getTranslationOrFallback(
        t,
        `reason_${item.reasonCode}`,
        item.reason || item.reasonCode || "-"
    );
};

const BulkActionsStep3SendWhatsappTemplate = (props: IProps) => {
    const moduleName = "ELASTICLISTSETTINGSBULKSENDWHATSAPP";
    const {
        formikRef,
        listBulk,
        setStepBulk,
        setLoadingButtons,
        fromJobViewJobId,
        bulkActionType = "applicant_sendWhatsappTemplate",
        fixedGlobalTemplateKey = ""
    } = props;
    const classes = useStyles();
    const queryClient = useQueryClient();
    const [listData] = useList();
    const showMessage = useShowMessage();
    const { company } = useAuthState();
    const { isFeatureAuthorized } = useAuthorization();
    const [metadata] = useGetMetadata(moduleName);
    const [t, , readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const jobData = useJobListDropdown();
    const [bulkName, setBulkName] = React.useState("");
    const [selectedTemplateKey, setSelectedTemplateKey] = React.useState("");
    const [selectedJobId, setSelectedJobId] = React.useState(
        fromJobViewJobId || ""
    );
    const [preview, setPreview] = React.useState<PreviewData | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [previewError, setPreviewError] = React.useState("");
    const [recipientsPage, setRecipientsPage] = React.useState(0);
    const isScreeningQuestionnaireRequest = Boolean(fixedGlobalTemplateKey);

    const [connectionState, fetchConnection, , isConnectionFetching] =
        useApiCache(
            "/whatsapp/connection",
            "post",
            {
                companyId: company.companyId
            },
            false,
            1000 * 60 * 2
        );
    const [
        templatesState,
        fetchTemplates,
        invalidateTemplates,
        isTemplatesFetching
    ] = useApiCache(
        "/whatsapp/templates",
        "post",
        {
            companyId: company.companyId
        },
        false,
        1000 * 60 * 5,
        false,
        undefined,
        {
            refetchOnMount: isScreeningQuestionnaireRequest ? "always" : true
        }
    );
    const fetchConnectionRef = React.useRef(fetchConnection);
    const fetchTemplatesRef = React.useRef(fetchTemplates);

    fetchConnectionRef.current = fetchConnection;
    fetchTemplatesRef.current = fetchTemplates;

    React.useEffect(() => {
        fetchConnectionRef.current({
            companyId: company.companyId
        });
        fetchTemplatesRef.current({
            companyId: company.companyId
        });
    }, [company.companyId]);

    React.useEffect(() => {
        setSelectedJobId(fromJobViewJobId || "");
    }, [fromJobViewJobId]);

    React.useEffect(() => {
        setRecipientsPage(0);
    }, [preview]);

    const selectedIds = React.useMemo(
        () =>
            listBulk
                .filter((item) => item.checked)
                .map((item) => item.value)
                .filter(Boolean),
        [listBulk]
    );
    const selectedIdsKey = selectedIds.join("|");
    const recipientsRowsPerPage = 10;
    const previewItems = preview?.items || [];
    const pagedPreviewItems = previewItems.slice(
        recipientsPage * recipientsRowsPerPage,
        recipientsPage * recipientsRowsPerPage + recipientsRowsPerPage
    );
    const connectionData = getWhatsappApiResponseData(
        connectionState.response
    );
    const canSendMessages =
        company.whatsappEnabled === true &&
        connectionData.connected === true &&
        connectionData.featureEnabled !== false &&
        connectionData.canSendMessages !== false;
    const companyTemplates = React.useMemo(
        () =>
            getWhatsappApiResponseData(templatesState.response).templates || [],
        [templatesState.response]
    );
    const approvedTemplates = React.useMemo(
        () =>
            companyTemplates
                .filter((item: Compleo.IObject) => item.status === "APPROVED")
                .sort((left: Compleo.IObject, right: Compleo.IObject) =>
                    getWhatsappTemplateName(left).localeCompare(
                        getWhatsappTemplateName(right)
                    )
                ),
        [companyTemplates]
    );
    const screeningTemplateSetup = React.useMemo(
        () =>
            isScreeningQuestionnaireRequest
                ? getWhatsappScreeningQuestionnaireRequestTemplateSetup(
                      companyTemplates,
                      templatesState.status
                  )
                : null,
        [
            companyTemplates,
            isScreeningQuestionnaireRequest,
            templatesState.status
        ]
    );
    const selectableTemplates =
        screeningTemplateSetup?.selectableTemplates || approvedTemplates;
    const screeningTemplateSetupState =
        screeningTemplateSetup?.setupState || "READY";
    const screeningTemplateSetupNames =
        screeningTemplateSetup?.setupTemplates
            .map((template: Compleo.IObject) =>
                getWhatsappTemplateName(template)
            )
            .join(", ") || "";
    const canManageWhatsappSettings = isFeatureAuthorized(
        "ManageWhatsappSettings"
    );
    const selectedTemplate =
        selectableTemplates.find(
            (template: Compleo.IObject) =>
                getTemplateKey(template) === selectedTemplateKey
        ) || null;

    React.useEffect(() => {
        if (!isScreeningQuestionnaireRequest) {
            return;
        }

        setSelectedTemplateKey((current) => {
            const currentIsAvailable = selectableTemplates.some(
                (template: Compleo.IObject) =>
                    getTemplateKey(template) === current
            );
            return currentIsAvailable
                ? current
                : selectableTemplates[0]
                ? getTemplateKey(selectableTemplates[0])
                : "";
        });
    }, [isScreeningQuestionnaireRequest, selectableTemplates]);

    const templateAnalysis = analyseWhatsappTemplate(selectedTemplate);
    const templateContext = getWhatsappTemplateSendContext(selectedTemplate);
    const requiresJob =
        isScreeningQuestionnaireRequest || templateContext.requiresJob;
    const showJobSelector = requiresJob && !fromJobViewJobId;
    const hasRequiredJob = !requiresJob || !!selectedJobId;
    const previewTemplate = React.useMemo(
        () =>
            isScreeningQuestionnaireRequest
                ? getWhatsappScreeningQuestionnaireRequestPreviewTemplate(
                      selectedTemplate,
                      fixedGlobalTemplateKey
                  )
                : selectedTemplate
                  ? {
                        templateName: getWhatsappTemplateName(selectedTemplate),
                        languageCode: selectedTemplate.language || "pt_BR"
                    }
                  : null,
        [
            fixedGlobalTemplateKey,
            isScreeningQuestionnaireRequest,
            selectedTemplate
        ]
    );
    const hasSupportedPreviewTemplate = isScreeningQuestionnaireRequest
        ? templatesState.status === "success" &&
          Boolean(previewTemplate?.templateName)
        : Boolean(selectedTemplate && templateAnalysis.supported);

    React.useEffect(() => {
        let cancelled = false;

        const loadPreview = async () => {
            setPreview(null);
            setPreviewError("");

            if (
                !canSendMessages ||
                !previewTemplate ||
                !hasSupportedPreviewTemplate ||
                !hasRequiredJob ||
                selectedIds.length === 0
            ) {
                setPreviewLoading(false);
                return;
            }

            try {
                setPreviewLoading(true);
                const result = await apiDirectCall(
                    "/whatsapp/bulktemplatepreview",
                    "post",
                    {
                        companyId: company.companyId,
                        ids: selectedIds,
                        templateName: previewTemplate.templateName,
                        languageCode: previewTemplate.languageCode,
                        jobId: selectedJobId || undefined,
                        bulkActionType
                    }
                );
                if (!cancelled) {
                    const data = getWhatsappApiResponseData(result);
                    setPreview(data as PreviewData);
                }
            } catch (error) {
                if (!cancelled) {
                    setPreviewError(
                        getApiErrorMessage(error, t("previewError"))
                    );
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
        };
    }, [
        canSendMessages,
        bulkActionType,
        company.companyId,
        hasRequiredJob,
        hasSupportedPreviewTemplate,
        previewTemplate,
        selectedIdsKey,
        selectedJobId,
        t
    ]);

    const handleSubmit = React.useCallback(async () => {
        if (!selectedTemplate) {
            showMessage(
                t(
                    isScreeningQuestionnaireRequest
                        ? "screeningTemplateUnavailable"
                        : "requiredTemplate"
                ),
                "error"
            );
            setLoadingButtons(false);
            return;
        }
        if (!templateAnalysis.supported) {
            showMessage(t("unsupported"), "error");
            setLoadingButtons(false);
            return;
        }
        if (!hasRequiredJob) {
            showMessage(t("requiredJob"), "error");
            setLoadingButtons(false);
            return;
        }
        if (preview?.configurationBlockCode) {
            showMessage(
                t(`configuration_${preview.configurationBlockCode}`),
                "error"
            );
            setLoadingButtons(false);
            return;
        }
        if (!preview || preview.eligibleCount < 1) {
            showMessage(t("noRecipients"), "error");
            setLoadingButtons(false);
            return;
        }

        try {
            setLoadingButtons(true);
            await apiDirectCall("/bulkactions/baadd", "post", {
                companyId: company.companyId,
                ids: preview.eligibleIds,
                bulkSelectionId: listBulk[0]?.bulkSelectionId,
                bulkProtocolVersion: 1,
                name: bulkName,
                type: bulkActionType,
                templateName: getWhatsappTemplateName(selectedTemplate),
                languageCode: selectedTemplate.language || "pt_BR",
                jobId: selectedJobId || undefined,
                job: selectedJobId
                    ? {
                          value: selectedJobId
                      }
                    : undefined
            });
            queryClient.invalidateQueries(listData.tempInfo.useApiKey);
            showMessage(t("created"), "success");
            setStepBulk(99);
        } catch (error) {
            showMessage(
                getApiErrorMessage(
                    error,
                    t("createError")
                ),
                "error"
            );
        } finally {
            setLoadingButtons(false);
        }
    }, [
        bulkName,
        company.companyId,
        hasRequiredJob,
        isScreeningQuestionnaireRequest,
        listData.tempInfo.useApiKey,
        preview,
        queryClient,
        bulkActionType,
        selectedJobId,
        selectedTemplate,
        setLoadingButtons,
        setStepBulk,
        showMessage,
        t,
        templateAnalysis.supported
    ]);

    React.useEffect(() => {
        formikRef.current = {
            submitForm: handleSubmit
        };
    }, [formikRef, handleSubmit]);

    const templatesLoading =
        templatesState.status === "initial" ||
        templatesState.status === "fetching" ||
        isTemplatesFetching;
    const loading = templatesLoading || isConnectionFetching;
    const metadataReady = readyTranslation && metadata.status === "success";

    if (!metadataReady) {
        return <Loading />;
    }

    return (
        <div className={classes.root}>
            {!canSendMessages && !loading && (
                <Alert severity="warning">{t("noConnection")}</Alert>
            )}

            <TextField
                fullWidth
                label={t("operationName")}
                helperText={t("operationNameHelper")}
                value={bulkName}
                onChange={(event) => setBulkName(event.target.value)}
            />

            <TextField
                select
                fullWidth
                label={t("template")}
                value={selectedTemplateKey}
                onChange={(event) => setSelectedTemplateKey(event.target.value)}
                disabled={
                    !canSendMessages ||
                    (isScreeningQuestionnaireRequest &&
                        screeningTemplateSetupState !== "READY")
                }
            >
                {selectableTemplates.map((template: Compleo.IObject) => (
                    <MenuItem
                        key={getTemplateKey(template)}
                        value={getTemplateKey(template)}
                    >
                        {`${getWhatsappTemplateName(template)}${
                            template.category
                                ? ` (${t("category")}: ${getTranslationOrFallback(
                                      t,
                                      `templateCategory_${template.category}`,
                                      template.category
                                  )})`
                                : ""
                        }`}
                    </MenuItem>
                ))}
            </TextField>

            {isScreeningQuestionnaireRequest &&
                !loading &&
                screeningTemplateSetupState !== "READY" && (
                    <Alert
                        severity={
                            screeningTemplateSetupState === "PENDING"
                                ? "warning"
                                : "error"
                        }
                        action={
                            screeningTemplateSetupState === "ERROR" ? (
                                <Button
                                    color="inherit"
                                    onClick={() =>
                                        void invalidateTemplates?.({
                                            companyId: company.companyId
                                        })
                                    }
                                >
                                    {t("screeningTemplateRetry")}
                                </Button>
                            ) : canManageWhatsappSettings ? (
                                <Button
                                    color="inherit"
                                    onClick={() =>
                                        navigate("/settings/whatsapp")
                                    }
                                >
                                    {t("screeningTemplateSettings")}
                                </Button>
                            ) : undefined
                        }
                    >
                        {t(
                            `screeningTemplateSetup_${screeningTemplateSetupState}`,
                            {
                                templates: screeningTemplateSetupNames
                            }
                        )}
                        {!canManageWhatsappSettings &&
                            screeningTemplateSetupState !== "ERROR" && (
                                <Typography variant="body2">
                                    {t("screeningTemplateAskAdmin")}
                                </Typography>
                            )}
                    </Alert>
                )}

            {showJobSelector && (
                <TextField
                    select
                    fullWidth
                    label={t("job")}
                    value={selectedJobId}
                    onChange={(event) => setSelectedJobId(event.target.value)}
                    disabled={!jobData.ready}
                >
                    {jobData.data.map((job: Compleo.IObject) => (
                        <MenuItem key={job.value} value={job.value}>
                            {job.label}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {loading && <Loading />}

            {selectedTemplate && (
                <Alert severity="info">{t("automaticParameters")}</Alert>
            )}

            {selectedTemplate && !templateAnalysis.supported && (
                <Alert severity="warning">{t("unsupported")}</Alert>
            )}

            {selectedTemplate && requiresJob && !selectedJobId && (
                <Alert severity="warning">{t("requiredJob")}</Alert>
            )}

            <div className={classes.previewBox}>
                {previewLoading ? (
                    <Loading />
                ) : previewError ? (
                    <Alert severity="error">{previewError}</Alert>
                ) : preview ? (
                    preview.configurationBlockCode ? (
                        isScreeningQuestionnaireRequest &&
                        screeningTemplateSetupState !== "READY" &&
                        preview.configurationBlockCode ===
                            "SCREENING_TEMPLATE_UNAVAILABLE" ? (
                            <Typography variant="body2" color="textSecondary">
                                {t("previewPending")}
                            </Typography>
                        ) : (
                            <Alert severity="error">
                                {t(
                                    `configuration_${preview.configurationBlockCode}`
                                )}
                            </Alert>
                        )
                    ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("selected")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.total}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("eligible")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.eligibleCount}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.stat}>
                                <Typography variant="caption" color="textSecondary">
                                    {t("blocked")}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    className={classes.statValue}
                                >
                                    {preview.blockedCount}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                {t("optInType")}:{" "}
                                {getTranslationOrFallback(
                                    t,
                                    `consent_${preview.requiredConsentCategory}`,
                                    preview.requiredConsentCategory
                                )}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {t("eligibleOnlyNotice")}
                            </Typography>
                            {Object.keys(preview.reasons || {}).length > 0 && (
                                <Typography variant="body2" color="textSecondary">
                                    {Object.entries(preview.reasons)
                                        .map(
                                            ([reason, total]) =>
                                                `${total} ${
                                                    t(`reason_${reason}`)
                                                }`
                                        )
                                        .join(", ")}
                                </Typography>
                            )}
                            {previewItems.length > 0 && (
                                <div className={classes.recipientsTableWrapper}>
                                    <Table
                                        size="small"
                                        className={classes.recipientsTable}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {t("candidateName")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateEmail")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateMobile")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("candidateStatus")}
                                                </TableCell>
                                                <TableCell>
                                                    {t("blockReason")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pagedPreviewItems.map((item) => (
                                                <TableRow
                                                    key={
                                                        item.id ||
                                                        item.applicantId
                                                    }
                                                >
                                                    <TableCell>
                                                        {item.name ||
                                                            item.applicantId ||
                                                            "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.email || "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.mobileNumber ||
                                                            "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.eligible
                                                            ? t(
                                                                  "statusEligible"
                                                              )
                                                            : t(
                                                                  "statusBlocked"
                                                              )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getPreviewReasonLabel(
                                                            t,
                                                            item
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {previewItems.length > recipientsRowsPerPage && (
                                        <TablePagination
                                            component="div"
                                            count={previewItems.length}
                                            page={recipientsPage}
                                            rowsPerPage={recipientsRowsPerPage}
                                            rowsPerPageOptions={[]}
                                            onChangePage={(_, page) =>
                                                setRecipientsPage(page)
                                            }
                                            labelRowsPerPage=""
                                            labelDisplayedRows={({ from, to, count }) =>
                                                t("paginationDisplayedRows", {
                                                    from,
                                                    to,
                                                    count
                                                })
                                            }
                                            backIconButtonText={t(
                                                "previousPage"
                                            )}
                                            nextIconButtonText={t("nextPage")}
                                        />
                                    )}
                                </div>
                            )}
                        </Grid>
                    </Grid>
                    )
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        {selectedTemplateKey
                            ? t("previewPending")
                            : t("selectTemplatePreview")}
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default BulkActionsStep3SendWhatsappTemplate;
